// Libraries
import moment from 'moment';
import { useEffect, useState } from 'react';
import { isNotNil } from 'ramda';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

// MUI
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import { ResourceTextApplication } from '../../core/resources';

// Common
import { ASwitch } from '../../common/inputs';

// Interfaces
import {
    FarmSettingTypeEnum,
    IFarmSettings,
    IFarmSettingsUpdatePayload,
    IStoreFarmSettingsPayload,
} from '../../interfaces/IFarmSettings';
import { IApiResult } from '../../interfaces/IGeneral';
import { EmptyGUID } from '../../interfaces/types';

// Store
import { useAppSelector } from '../../store/hooks';

// API
import farmSettingsApi from '../../api/farm/farmSettingsApi';
import animalWaitingRoomApi from '../../api/animalWaitingRoomApi';

interface IPreliminaryAnnouncementsSwitchProps {
    farmId: string | null | undefined;
}

export default function PreliminaryAnnouncementsSwitch({
    farmId,
}: IPreliminaryAnnouncementsSwitchProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaCompany', 'AnelmaSolmuErrors']);
    const { enqueueSnackbar } = useSnackbar();
    const userData = useAppSelector((state) => state.userData.data);

    const preliminaryAnnouncementSwitchId = 'anelma-preliminary-announcement-switch';
    const dateTimeFormat = 'YYYY-MM-DDTHH:mm:ss';

    const [loading, setLoading] = useState<boolean>(false);
    const [preliminaryAnnouncementsInUse, setPreliminaryAnnouncementsInUse] =
        useState<boolean>(false);
    const [farmSettings, setFarmSettings] = useState<IFarmSettings>();

    useEffect(() => {
        getPreliminaryAnnouncementValue();
    }, []);

    const getPreliminaryAnnouncementValue = () => {
        if (isNotNil(farmId)) {
            setLoading(true);

            farmSettingsApi.getFarmSettings(farmId).then((response) => {
                if (!response) {
                    enqueueSnackbar(`${t('AnelmaSolmuErrors:1000')}, 'error'`);
                    setLoading(false);
                    return;
                }

                const isPreliminaryAnnouncementsInUse =
                    response?.Entity.SettingType ===
                    FarmSettingTypeEnum.PreliminaryAnnouncementsInUse
                        ? response.Entity.SettingValue
                        : 0;
                setPreliminaryAnnouncementsInUse(
                    isPreliminaryAnnouncementsInUse === 1 ? true : false
                );
                if (response.Entity.FarmGuid !== EmptyGUID) {
                    setFarmSettings(response.Entity);
                }
                setLoading(false);
            });
        }
    };

    const handlePreliminaryAnnouncementSwitchChange = (value: boolean) => {
        if (isNotNil(farmId)) {
            if (isNotNil(farmSettings)) {
                setLoading(true);
                farmSettingsApi
                    .updateFarmSettings(getUpdateFarmSettingsPayload(value, farmId))
                    .then((response) => {
                        if (value) {
                            animalWaitingRoomApi
                                .emptyProducerWaitingRoom(farmId)
                                .then((animalResponse) => {
                                    setPreliminaryAnnouncementsInUse(value);
                                    handleUpdateResponse(response);
                                });
                        }
                        else {
                            setPreliminaryAnnouncementsInUse(value);
                            handleUpdateResponse(response);
                        }
                    });
            } else if (value) {
                setLoading(true);
                farmSettingsApi
                    .storeFarmSettings(getStoreFarmSettingsPayload(farmId))
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(`${t('AnelmaSolmuErrors:1000')}, 'error'`);
                            setLoading(false);
                            return;
                        }

                        animalWaitingRoomApi
                            .emptyProducerWaitingRoom(farmId)
                            .then((animalResponse) => {
                                setPreliminaryAnnouncementsInUse(true);
                                setFarmSettings(response.Entity);
                                setLoading(false);

                                enqueueSnackbar(t('AnelmaCompany:1131'), {
                                    variant: 'success',
                                });
                            });
                    });
            }
        }
    };

    const getStoreFarmSettingsPayload = (farmId: string): IStoreFarmSettingsPayload => {
        return {
            FarmGuid: farmId,
            SettingType: FarmSettingTypeEnum.PreliminaryAnnouncementsInUse,
            SettingValue: 1,
        };
    };

    const getUpdateFarmSettingsPayload = (
        preliminarySettingValue: boolean,
        farmId: string
    ): IFarmSettingsUpdatePayload => {
        return {
            FarmGuid: farmId,
            SettingType: FarmSettingTypeEnum.PreliminaryAnnouncementsInUse,
            SettingValue: preliminarySettingValue ? 1 : 0,
            ValidityEnd: !preliminarySettingValue ? moment().utc().format(dateTimeFormat) : null,
        };
    };

    const handleUpdateResponse = (response: IApiResult<IFarmSettings> | null) => {
        if (!response) {
            enqueueSnackbar(`${t('AnelmaSolmuErrors:1000')}, 'error'`);
            setLoading(false);
            return;
        }

        enqueueSnackbar(t('AnelmaCompany:1132'), { variant: 'success' });
        setLoading(false);
    };

    return (
        <Grid sx={{ marginBottom: '2em' }}>
            <Typography variant='h4'>{t('AnelmaCompany:1130')}</Typography>
            <Grid size={{ md: 12 }}>
                <ASwitch
                    id={preliminaryAnnouncementSwitchId}
                    data-robot-id={preliminaryAnnouncementSwitchId}
                    label={t('AnelmaCompany:1129')}
                    checked={preliminaryAnnouncementsInUse}
                    onChange={handlePreliminaryAnnouncementSwitchChange}
                    disabled={loading || !userData.IsSnellmanUser}
                    withoutContainer
                />
            </Grid>
        </Grid>
    );
}
